import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CalendarClock, ThumbsUp } from "lucide-react";
import { Button } from "@baluu/ui/components/button";
import { Badge } from "@baluu/ui/components/badge";
import { Textarea } from "@baluu/ui/components/textarea";
import { Spinner } from "@baluu/ui/components/spinner";
import type { ViewContext } from "../types";
import { BookingApiError, bookingJson, messageOf, sendJson } from "../api";
import { BookingLoading, BookingProblem, Notice, RecordNotFound } from "../parts";
import { isOtherSchool } from "../school";
import { useAuthSnapshot } from "~/widgets/lib/auth-client";
import { formatMoney } from "../money";
import { BackToBooking, ClassSummary } from "./reschedule/FlowParts";
import { authInit } from "./reschedule/request";
import { formatSessionDateTime } from "./reschedule/sessions";
import {
  CANCEL_MESSAGE_MAX,
  cancelPayload,
  cancelState,
  cancelTitle,
  guestsLabel,
  isUnconfirmedRequest,
  offersRescheduleInstead,
  refundLines,
  refundNote,
  type CancelPageData,
} from "./cancel/cancelState";

// /bookings/cancel/<id>: cancel a booking. A port of v1's cancel page
// (BookingCancelClient + its four views): the refund it comes with and a
// required reason, the nudge to reschedule instead, the policy's "no", an
// already-cancelled booking, and the confirmation once it's done.

const CANCEL_FAILED =
  "Something went wrong cancelling your booking. Please try again, and if this keeps happening, contact us.";

export function CancelView({ ctx, bookingId }: { ctx: ViewContext; bookingId: string }) {
  const { token } = useAuthSnapshot();
  const qc = useQueryClient();
  const [message, setMessage] = useState("");
  const [touched, setTouched] = useState(false);
  const [cancelled, setCancelled] = useState(false);

  const url = `${ctx.api}/widget/pages/bookings/${encodeURIComponent(bookingId)}/cancel`;
  const page = useQuery({
    queryKey: ["booking", "cancel", ctx.api, bookingId, token],
    queryFn: () => bookingJson<CancelPageData>(url, authInit(token)),
    retry: (count, error) => !(error instanceof BookingApiError && error.status < 500) && count < 2,
  });

  const cancel = useMutation({
    mutationFn: (text: string) => sendJson(url, "PUT", cancelPayload(text), authInit(token)),
    onSuccess: () => {
      setCancelled(true);
      qc.invalidateQueries({ queryKey: ["booking"], predicate: (q) => q.queryKey[1] !== "cancel" });
    },
  });

  const elsewhere = !!page.data && isOtherSchool(ctx.businessId, page.data.booking?.school, page.data.course?.schoolId);
  const isMarketplace = !elsewhere && page.data?.booking?.source === "obby";
  useEffect(() => {
    if (isMarketplace) ctx.toMarketplace(`/bookings/cancel/${bookingId}`);
  }, [isMarketplace, ctx, bookingId]);

  const back = <BackToBooking href={ctx.links.manage(bookingId)} />;
  const terms = ctx.links.terms();

  if (page.isLoading || isMarketplace) return <BookingLoading rows={3} />;
  if (page.isError) {
    const status = page.error instanceof BookingApiError ? page.error.status : 0;
    return status === 404 || status === 401 || status === 403 ? (
      <RecordNotFound />
    ) : (
      <BookingProblem
        title="We couldn’t load this booking"
        message={messageOf(page.error, "Please try again in a moment.")}
        onRetry={() => page.refetch()}
      />
    );
  }

  if (elsewhere) return <RecordNotFound />;
  const data = page.data!;
  const booking = data.booking;
  const currency = String(booking.currencyUsed || data.course?.currency || "GBP").toUpperCase();

  if (cancelled) {
    const refund = refundLines(booking, data.refundPercentage);
    return (
      <div className="space-y-4">
        {back}
        <div className="mx-auto max-w-lg space-y-3 rounded-lg border bg-card p-8 text-center">
          <ThumbsUp className="mx-auto size-12 text-primary" aria-hidden="true" />
          <h1 className="text-2xl font-semibold">All done. You’ve cancelled your booking</h1>
          <p className="text-sm text-muted-foreground">
            {!isUnconfirmedRequest(booking) && refund.cardRefund > 0
              ? `${formatMoney(refund.cardRefund, currency)} will be back in your bank account in 5-10 business days.`
              : "We’ve emailed you a confirmation."}
          </p>
        </div>
      </div>
    );
  }

  const state = cancelState(data);

  if (state === "cancelled") {
    return (
      <div className="space-y-4">
        {back}
        <div className="mx-auto max-w-lg space-y-3 rounded-lg border bg-card p-8 text-center">
          <ThumbsUp className="mx-auto size-12 text-primary" aria-hidden="true" />
          <h1 className="text-2xl font-semibold">This booking has been cancelled</h1>
        </div>
      </div>
    );
  }

  if (state === "cant-cancel") {
    return (
      <div className="space-y-4">
        {back}
        <div className="mx-auto max-w-lg space-y-3 rounded-lg border bg-card p-8 text-center">
          <h1 className="text-2xl font-semibold">Cancel booking</h1>
          <p>
            This booking can’t be refunded under our cancellation policy.{" "}
            <a href={terms} className="font-medium text-primary underline underline-offset-4">
              Read our full terms of booking
            </a>
            .
          </p>
          {offersRescheduleInstead(booking, data.canReschedule) && (
            <p>
              You can still reschedule it.{" "}
              <a href={ctx.links.reschedule(bookingId)} className="font-medium text-primary underline underline-offset-4">
                Reschedule your booking
              </a>
            </p>
          )}
        </div>
      </div>
    );
  }

  const refund = refundLines(booking, data.refundPercentage);
  const unconfirmed = isUnconfirmedRequest(booking);
  const missingReason = !message.trim();

  return (
    <div className="space-y-4">
      {back}
      <div className="mx-auto max-w-lg space-y-6">
        <div className="space-y-3 text-center">
          <h1 className="text-2xl font-semibold">{cancelTitle(booking)}</h1>
        </div>
        <ClassSummary title={data.course?.title} thumbnail={data.course?.thumbnail}>
          {data.session && formatSessionDateTime(data.session, data.course?.timezone || booking.timezone || "")}
          {data.session && " • "}
          {guestsLabel(booking.totalAttendants)}
        </ClassSummary>

        {offersRescheduleInstead(booking, data.canReschedule) && (
          <a
            href={ctx.links.reschedule(bookingId)}
            className="flex items-start gap-3 rounded-lg border bg-muted/40 p-4 transition-colors hover:bg-muted"
          >
            <CalendarClock className="mt-0.5 size-5 shrink-0" aria-hidden="true" />
            <span className="space-y-1">
              <span className="block font-semibold">Reschedule to support a small business</span>
              <span className="block text-sm text-muted-foreground">
                Please consider rescheduling your booking instead, where possible, to support a small business.
              </span>
              <span className="block text-sm font-medium text-primary underline underline-offset-4">Reschedule your booking</span>
            </span>
          </a>
        )}

        <section className="space-y-3 rounded-lg border bg-card p-6">
          <h2 className="text-lg font-semibold">Refund details</h2>
          {unconfirmed ? (
            <p className="text-sm">
              The school hasn’t confirmed this request yet, so no payment has been taken. Cancelling withdraws your request.
            </p>
          ) : (
            <>
              <p className="text-sm">{refundNote(booking)}</p>
              {refund.card > 0 && (
                <dl className="divide-y border-y text-sm">
                  <div className="flex justify-between py-3">
                    <dt>Card</dt>
                    <dd>{formatMoney(refund.card, currency)}</dd>
                  </div>
                  <div className="flex justify-between py-3 font-semibold">
                    <dt>Total refund to your bank account</dt>
                    <dd>{formatMoney(refund.cardRefund, currency)}</dd>
                  </div>
                </dl>
              )}
              <p className="text-sm text-muted-foreground">
                Cancellations are effective immediately. Refunds are processed within 5-10 business days.
              </p>
            </>
          )}
        </section>

        <section className="space-y-3 rounded-lg border bg-card p-6">
          <div className="flex items-center gap-2">
            <label htmlFor="cancel-reason" className="text-lg font-semibold">
              Reason for cancelling
            </label>
            <Badge variant="destructive" className="text-xs">
              Required
            </Badge>
          </div>
          <p className="text-sm text-muted-foreground">
            Please let us know why you’re cancelling. Your feedback helps us improve.
          </p>
          <Textarea
            id="cancel-reason"
            value={message}
            maxLength={CANCEL_MESSAGE_MAX}
            placeholder="Please share your reason for cancelling…"
            className="min-h-[100px] resize-none"
            aria-invalid={touched && missingReason ? true : undefined}
            aria-describedby="cancel-reason-help"
            onChange={(e) => setMessage(e.target.value)}
            onBlur={() => setTouched(true)}
            disabled={cancel.isPending}
            required
          />
          <div id="cancel-reason-help" className="flex items-center justify-between text-xs">
            {touched && missingReason ? <span className="text-destructive">Please give a reason for cancelling</span> : <span />}
            <span className="text-muted-foreground">
              {message.length}/{CANCEL_MESSAGE_MAX} characters
            </span>
          </div>
        </section>

        {cancel.isError && (
          <Notice tone="error">
            {cancel.error instanceof BookingApiError && cancel.error.status < 500
              ? messageOf(cancel.error, CANCEL_FAILED)
              : CANCEL_FAILED}
          </Notice>
        )}

        <div className="flex flex-wrap items-center justify-between gap-3">
          {booking.usedBundleID ? (
            <a
              href={ctx.links.bundle(String(booking.usedBundleID))}
              className="inline-flex h-9 items-center justify-center rounded-md border bg-background px-4 text-sm font-medium hover:bg-muted"
            >
              View my bundle credits
            </a>
          ) : (
            <span />
          )}
          <Button
            variant="destructive"
            disabled={missingReason || cancel.isPending}
            onClick={() => {
              setTouched(true);
              if (!missingReason) cancel.mutate(message);
            }}
          >
            {cancel.isPending && <Spinner className="mr-2 size-4" />}
            {cancel.isPending ? "Cancelling…" : cancelTitle(booking)}
          </Button>
        </div>
      </div>
    </div>
  );
}
